import './styles.css'
import { useSnapshot } from 'valtio'
import { useEffect, useRef, useState, useCallback } from 'react'
import { state } from '../store'

// Section ID lar
const SECTIONS = {
  home: 'section-1',
  products: 'section-2',
  services: 'section-4',
  contact: 'footer'
}

export default function Overlay() {
  const { colors, selectedColor, autoRotate } = useSnapshot(state)
  const timerRef = useRef(null)
  const pauseTimeoutRef = useRef(null)
  const audioRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)

  // Rangni avtomatik o'zgartirish
  useEffect(() => {
    if (autoRotate) {
      timerRef.current = setInterval(() => {
        const randomColor = colors[Math.floor(Math.random() * colors.length)]
        state.selectedColor = randomColor
      }, 5000)
    }
    return () => clearInterval(timerRef.current)
  }, [autoRotate, colors])

  // Rang tanlash
  const handleColorClick = useCallback((color) => {
    state.selectedColor = color
    state.autoRotate = false
    clearInterval(timerRef.current)
    clearTimeout(pauseTimeoutRef.current)
    pauseTimeoutRef.current = setTimeout(() => {
      state.autoRotate = true
    }, 10000)
  }, [])

  // Musiqa toggle
  const toggleMusic = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return

    if (!isPlaying) {
      audio.play().catch((err) => console.error('Audio play error:', err))
      setIsPlaying(true)
    } else {
      audio.pause()
      setIsPlaying(false)
    }
  }, [isPlaying])

  // Section ga scroll qilish
  const scrollToSection = useCallback((sectionId) => {
    const element = document.querySelector(`.${sectionId}`) || document.querySelector(sectionId)
    if (element) {
      const top = element.getBoundingClientRect().top + window.scrollY
      window.scrollTo({
        top: top,
        behavior: 'smooth'
      })
    }
  }, [])

  // Microsoft Store ga o'tish
  const handleBuyClick = useCallback(() => {
    window.open('https://www.microsoft.com/en-us/d/xbox-elite-wireless-controller-series-2/8rsn7m7ktxf9', '_blank')
  }, [])

  // Details section ga scroll
  const handleDetailsClick = useCallback(() => {
    scrollToSection(SECTIONS.products)
  }, [scrollToSection])

  return (
    <div className="container">
      <header>
        <div className="brand" onClick={() => scrollToSection(SECTIONS.home)}>
          <img src="/logo.svg" alt="Logo" width="120" height="36" />
        </div>

        <nav className="header-nav">
          <ul>
            <li onClick={() => scrollToSection(SECTIONS.home)}>Home</li>
            <li onClick={() => scrollToSection(SECTIONS.products)}>Products</li>
            <li onClick={() => scrollToSection(SECTIONS.services)}>Services</li>
            <li onClick={() => scrollToSection(SECTIONS.contact)}>Contact</li>
          </ul>
        </nav>

        <button onClick={toggleMusic}>
          {isPlaying ? 'PAUSE' : 'SOUND'}
        </button>
        <audio ref={audioRef} src="/audio/music.mp3" preload="auto" loop />
      </header>

      <main className="main-wrapper">
        <section className="section section-1">
          <div className="section-1-wrapper">
            <h1 className="section-1-title">Keep up <br /> with the era</h1>
            <p className="section-1-text">
              Change your gaming experience to the next level with our product
            </p>
            <div className="section-1-buttons">
              <button onClick={handleBuyClick}>$70 From Microsoft</button>
              <button onClick={handleDetailsClick}>MORE DETAILS</button>
            </div>
          </div>

          <div className="section-1-colors-wrapper">
            <ul>
              {colors.map((color) => (
                <li
                  key={color}
                  style={{ backgroundColor: color }}
                  className={selectedColor === color ? 'selected' : ''}
                  onClick={() => handleColorClick(color)}
                />
              ))}
            </ul>
          </div>

          <div className="scroll-indicator" onClick={() => scrollToSection(SECTIONS.products)}>
            <div className="scroll-icon"></div>
          </div>
        </section>

        <section className="section section-2">
          <div className="section-2-wrapper">
            <div className="section-2-info">
              <h2>Play like a pro</h2>
              <hr />
              <p>
                Compete at the next level with included interchangeable components
                that unlock limitless customization in the games you love the most.
              </p>
            </div>
          </div>
        </section>

        <section className="section section-3">
          <div className="section-3-wrapper">
            <h2>Refined components that are built to last</h2>
            <div className="section-3-info">
              <h3>Built For <br /> Performance</h3>
              <hr />
              <p>
                Designed to meet the needs of today's competitive gamers, the Xbox
                Elite Wireless Controller Series 2 features over 30 new ways to
                play like a pro. Enhance your aiming with new adjustable-tension
                thumbsticks, fire even faster with shorter hair trigger locks, and
                stay on target with a wrap-around rubberized grip.
              </p>
            </div>
          </div>
        </section>

        <section className="section section-4">
          <div className="section-4-wrapper">
            <div className="section-4-info-wrapper">
              <h2>Optimized for you</h2>
              <hr />
              <p>
                Experience limitless customization with exclusive button mapping
                options in the Xbox accessories app. Save up to 3 custom profiles
                and 1 default profile on the controller and switch between them on
                the fly with the Profile button.
              </p>
            </div>
            <video autoPlay loop muted playsInline>
              <source src="/videos/1.mp4" type="video/mp4" />
            </video>
          </div>
        </section>

        <section className="section section-5">
          <div className="section-5-wrapper">
            <div className="section-5-info-wrapper">
              <h2>Customize with interchangeable components</h2>
              <hr />
              <p>
                Swap thumbstick toppers, D-pads, and paddles to tailor your
                controller to your preferred gaming style.
              </p>
            </div>
            <video autoPlay loop muted playsInline>
              <source src="/videos/2.mp4" type="video/mp4" />
            </video>
          </div>
        </section>

        <section className="section section-6">
          <div className="section-6-wrapper">
            <div className="section-6-info-wrapper">
              <h2>Switch between devices</h2>
              <hr />
              <p>
                Easily pair and switch between Xbox Series X|S and Xbox One
                consoles, Windows PC, and mobile devices with Xbox Wireless and
                Bluetooth.
              </p>
            </div>
            <video autoPlay loop muted playsInline>
              <source src="/videos/3.mp4" type="video/mp4" />
            </video>
          </div>
        </section>

        <section className="section section-7">
          <div className="section-7-wrapper">
            <h2>Plays great with Elite</h2>
            <div className="section-7-info">
              <h3>Halo Infinite</h3>
              <hr />
              <p>
                Use custom profile management to match your favorite loadouts to
                your preferred button configurations. Dial in your aim with
                adjustable tension thumbsticks.
              </p>
            </div>
          </div>
        </section>

        <section className="section section-8">
          <div className="section-8-wrapper">
            <div className="section-8-info">
              <h3>Forza Horizon 5</h3>
              <hr />
              <p>
                Hit the apex in full control with adjustable tension thumbsticks
                to find the perfect handling in the car of your dreams. Master
                manual shifting with interchangeable paddles that can be
                customized with the Xbox Accessories app.
              </p>
            </div>
          </div>
        </section>
      </main>

      <footer>
        <div className="footer-top">
          <div className="footer-info-wrapper">
            <ul>
              <h3>Navigation</h3>
              <li onClick={() => scrollToSection(SECTIONS.home)}>Home</li>
              <li onClick={() => scrollToSection(SECTIONS.products)}>Products</li>
              <li onClick={() => scrollToSection(SECTIONS.services)}>Services</li>
              <li onClick={() => scrollToSection(SECTIONS.contact)}>Contact</li>
            </ul>
            <ul>
              <h3>Legal</h3>
              <li>Terms of Service</li>
              <li>Privacy Policy</li>
              <li>Cookie Policy</li>
            </ul>
            <ul>
              <h3>Support</h3>
              <li>FAQ</li>
              <li>Help Center</li>
              <li>Warranty</li>
            </ul>
          </div>

          <div className="footer-brand" onClick={() => scrollToSection(SECTIONS.home)}>
            <div className="footer-h2">
              <img src="/logo.svg" alt="Xbox Logo" width="50" height="50" />
              <h2>XBOX</h2>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <p className="copyright">© 2025 Unfor Dev. All rights reserved.</p>
          <div className="footer-social">
            <a href="#" aria-label="Twitter">𝕏</a>
            <a href="#" aria-label="Instagram">◎</a>
            <a href="#" aria-label="YouTube">▶</a>
          </div>
        </div>
      </footer>
    </div>
  )
}
